import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik } from '@strata/react-inputs';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { put } from 'services/Http';

const getNextStatus = (status) => {
    switch (status) {
        case ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE:
        case ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE:
            return ClinicalPolicyStatusIndicator.Statuses.PEND_PROD;
        case ClinicalPolicyStatusIndicator.Statuses.PEND_PROD:
            return ClinicalPolicyStatusIndicator.Statuses.DRAFT;
        case ClinicalPolicyStatusIndicator.Statuses.IN_PROD:
            return ClinicalPolicyStatusIndicator.Statuses.RETIRED;
        default:
            return undefined;
    }
};

const getDialogTitle = (status) => {
    switch (status) {
        case ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE:
        case ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE:
            return 'Move To Pending Production';
        case ClinicalPolicyStatusIndicator.Statuses.PEND_PROD:
            return 'Send Back To Draft Status';
        case ClinicalPolicyStatusIndicator.Statuses.IN_PROD:
            return 'Deactivate Policy';
        default:
            return '';
    }
};

const getDialogContent = (status) => {
    switch (status) {
        case ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE:
        case ClinicalPolicyStatusIndicator.Statuses.EXTAPPROVE:
            return 'Are you sure you want to move this draft to Pending Production?';
        case ClinicalPolicyStatusIndicator.Statuses.PEND_PROD:
            return 'Are you sure you want to send this policy back to Draft status?';
        case ClinicalPolicyStatusIndicator.Statuses.IN_PROD:
            return (
                <>
                    <p>Deactivating this policy will remove it from production.</p>
                    Are you sure you want to continue?
                </>
            );
        default:
            return undefined;
    }
};

export const NextPolicyStatusDialog = ({
    isOpen,
    fetchPolicy,
    onSecondaryButtonClick,
    currentStatus = undefined,
    payerId,
    draftId,
}) => (
    <Formik
        initialValues={{}}
        enableReinitialize
        validationSchema={Yup.object({
            comment: Yup.string(),
        })}
        onSubmit={async ({ comment }, { resetForm }) => {
            try {
                await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}/next`, {
                    policy_status: getNextStatus(currentStatus),
                    comment,
                });
                await fetchPolicy();
                resetForm();
                onSecondaryButtonClick();
            } catch (err) {
                handleError('Error updating policy status.', err);
            }
        }}
    >
        {(formik) => (
            <DialogWithForm
                dialogTitle={getDialogTitle(currentStatus)}
                primaryButtonLabel="Yes, Continue"
                secondaryButtonLabel="No, Cancel"
                isOpen={isOpen}
                onSecondaryButtonClick={() => {
                    formik.handleReset();
                    onSecondaryButtonClick();
                }}
                isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                dialogContent={
                    <>
                        <div className="margin__bottom--large">
                            {getDialogContent(currentStatus)}
                        </div>
                        <TextAreaFormik id="comment" label="Comment" />
                    </>
                }
            />
        )}
    </Formik>
);

NextPolicyStatusDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    fetchPolicy: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    currentStatus: PropTypes.oneOf(Object.values(ClinicalPolicyStatusIndicator.Statuses)),
    payerId: PropTypes.string.isRequired,
    draftId: PropTypes.string.isRequired,
};
